import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  ticker?: string | null;
  sector?: string | null;
  description?: string;
}

export const SEO: React.FC<SEOProps> = ({ ticker, sector, description }) => {
  const { t } = useTranslation();
  
  const siteTitle = t('header.title');
  const defaultDesc = t('accessibility.header_desc');
  
  const title = ticker 
    ? `${ticker} Stock Analysis & Price Prediction | ${siteTitle}`
    : t('accessibility.header_title');
    
  const metaDesc = description || (ticker 
    ? `Deep technical and fundamental analysis for ${ticker}${sector ? ` in the ${sector} sector` : ''}. Get institutional-grade stock research and price diagnostics on ${siteTitle}.`
    : defaultDesc);

  const baseUrl = 'https://stockinvestingpro.com';
  const path = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '';
  const canonicalUrl = `${baseUrl}${path}`;
  const ogImageUrl = `${baseUrl}/feature-full.png`;

  // Shorter title for social cards (≤60 chars for OG, ≤70 for Twitter)
  const ogTitle = ticker 
    ? `${ticker} Fair Value & DCF | ${siteTitle}`
    : `${siteTitle} | Algorithmic Stock Diagnostics`;

  // JSON-LD structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": siteTitle,
    "url": baseUrl,
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web, Android",
    "description": defaultDesc,
    "author": {
      "@type": "Organization",
      "name": "Stock Investing Pro"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Algorithmic Stock Diagnostics",
      "Cycle-Normalized DCF Valuation",
      "Master Confluence FA/TA Rating",
      "Regime-Aware Technical Analysis",
      "Portfolio Concentration HHI Calculator",
      "SaaS Rule of 40 Screener",
      "REIT AFFO/FFO Conversion Tracking"
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={metaDesc} />
      <meta name="keywords" content="algorithmic stock diagnostics, master confluence stock rating, regime-aware technical indicators, cycle normalized dcf calculator, reit affo to ffo conversion, portfolio concentration hhi, saas rule of 40 stocks, stock conviction level calculator" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Stock Investing Pro" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content={ogImageUrl} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};
